import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Form, Formik, FormikErrors } from "formik";
import * as React from "react";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import ErrorMsg from "../../components/ErrorMsg";
import ProgressIndicator from "../../components/ProgressIndicator";
import strings from "../../config/localization";
import {
  useGetAllDesignationsQuery,
  useEditTemplateMutation,
} from "../../services/appln";
import { isPositive, setDifference } from "../util";
import Plane from "../../components/CustomFields/Plane";
import EditButton from "../../components/Buttons/EditButton";

interface FormData {
  name: string;
  assessments: any;
}

interface Props {
  id: string;
  initialValues: any;
}

export default function EditPersonnelTemplate(props: Props) {
  console.log("EditPersonnelTemplate, props:", props);

  const getDefaultValues = () => {
    return { designation: "", count: "" };
  };

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const [editTemplate, editTemplateParams] = useEditTemplateMutation();

  const [designationMap, setDesignationMap] = React.useState(new Map());

  // saving assessments outside formik values since the one inside values cannot be
  // used to remove already specified designations.
  const [assessments, setAssessments] = React.useState([getDefaultValues()]);

  const getDesignations = () => {
    const usedDesignations = assessments.map((e: any) => {
      return e["designation"];
    });
    return setDifference(Array.from(designationMap.keys()), usedDesignations);
  };

  const metaData = [
    {
      id: "designation",
      type: "autocomplete",
      options: getDesignations(),
      width: 300,
      label: strings.designation,
    },
    {
      id: "count",
      type: "text",
      width: 100,
      label: strings.count,
    },
  ];

  const validate = (assessments: any) => {
    const errors = [];
    if (assessments.length === 0) {
      errors.push(strings.formatString(strings.assessmentsEmpty));
    }
    for (let i = 0; i < assessments.length; i++) {
      if (!assessments[i].designation) {
        errors.push(strings.formatString(strings.designationEmpty, i + 1));
      }
      if (assessments[i].count === "") {
        errors.push(strings.formatString(strings.countEmpty, i + 1));
      } else if (!isPositive(assessments[i].count)) {
        errors.push(strings.formatString(strings.invalidCount, i + 1));
      }
    }
    return errors.length > 0 ? errors.join("\n") : undefined;
  };

  const designationApiResponse = useGetAllDesignationsQuery({
    page: 1,
    limit: 1000,
    search: "",
  });

  React.useEffect(() => {
    if (designationApiResponse.isSuccess) {
      setDesignationMap(
        new Map(
          designationApiResponse.data.data.map((e: any) => {
            return [e.name, e.id];
          }),
        ),
      );
    }
  }, [designationApiResponse]);

  const existingAssessments = props.initialValues.row.assessments.map(
    (e: any) => {
      return {
        count: e.count,
        designation: e.designationName,
      };
    },
  );

  console.log(
    "EditPersonnelTemplate, exitingAssessments:",
    existingAssessments,
  );

  const iv: FormData = {
    name: props.initialValues.row.name,
    assessments: existingAssessments,
  };

  console.log("EditPersonnelTemplate, editTemplateParams:", editTemplateParams);
  React.useEffect(() => {
    if (editTemplateParams.isSuccess) {
      setAssessments([getDefaultValues()]);
      handleClose();
    }
  }, [editTemplateParams]);

  return (
    <div>
      <EditButton onClickHandler={handleClickOpen} />
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">{strings.newTemplate}</DialogTitle>
        {editTemplateParams.isError && (
          <ErrorMsg msg={strings.failedToEditTemplate} />
        )}
        {editTemplateParams.isLoading && <ProgressIndicator />}
        <DialogContent>
          <Formik
            initialValues={iv}
            validate={(values) => {
              const errors: FormikErrors<FormData> = {};
              if (!values.name) {
                errors.name = strings.nameMandatory;
              }
              const assessmentErrors = validate(values.assessments);
              if (assessmentErrors !== undefined) {
                errors.assessments = validate(values.assessments);
              }
              return errors;
            }}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);
              const new_assessments = values.assessments.map(
                (assessment: any) => {
                  return {
                    count: assessment.count,
                    designationId: designationMap.get(assessment.designation),
                  };
                },
              );
              values.assessments = new_assessments;
              editTemplate({
                id: props.id,
                body: values,
              });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => (
              <Form>
                <Box sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="name"
                        name="name"
                        label={strings.name}
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Plane
                        metaData={metaData}
                        data={values.assessments}
                        validationErrors={errors.assessments}
                        onAdd={(assessments: any) => {
                          setFieldValue("assessments", [
                            ...assessments,
                            getDefaultValues(),
                          ]);
                        }}
                        onChange={(assessments: any) => {
                          console.log(
                            "EditPersonnelTemplate, assessments:",
                            assessments,
                          );
                          setFieldValue("assessments", assessments);
                          setAssessments(assessments);
                        }}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <ButtonGroup>
                        <Button
                          variant="contained"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {strings.save}
                        </Button>
                        <Button variant="contained" onClick={handleClose}>
                          {strings.cancel}
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  </Grid>
                </Box>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
}
